import { useGetGlowUpStats } from '@/api/dashboard/dashboard.hooks';
import { GlowUpExerciseStat } from '@repo/types';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { useState } from 'react';

type SortKey = 'completions' | 'completionsPercent';
type SortDir = 'asc' | 'desc';

const StatCard = ({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) => (
  <div className="rounded-lg border bg-white p-4 shadow-sm flex flex-col gap-1">
    <p className="text-sm text-zinc-600">{label}</p>
    <p className="text-2xl font-bold text-zinc-900">{value}</p>
    {sub && <p className="text-xs text-zinc-500">{sub}</p>}
  </div>
);

const SortableHeader = ({
  label,
  sortKey,
  currentSortKey,
  sortDir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  currentSortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
}) => {
  const isActive = currentSortKey === sortKey;
  const activeIcon = sortDir === 'desc' ? ArrowDown : ArrowUp;
  const Icon = isActive ? activeIcon : ArrowUpDown;
  return (
    <th
      className="px-4 py-2 text-left text-sm font-medium text-zinc-700 cursor-pointer select-none hover:bg-zinc-50"
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon
          size={14}
          className={isActive ? 'text-zinc-700' : 'text-zinc-300'}
        />
      </span>
    </th>
  );
};

export const GlowUpDashboard = () => {
  const { data, isLoading, isError } = useGetGlowUpStats();
  const [sortKey, setSortKey] = useState<SortKey>('completions');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  if (isLoading)
    return <div className="p-6 text-sm text-zinc-600">Chargement...</div>;
  if (isError || !data)
    return (
      <div className="p-6 text-sm text-red-500">Erreur lors du chargement</div>
    );

  const { userStats, exerciseStats } = data;

  const sortedExercises = [...exerciseStats].sort(
    (a: GlowUpExerciseStat, b: GlowUpExerciseStat) => {
      const diff = a[sortKey] - b[sortKey];
      return sortDir === 'desc' ? -diff : diff;
    },
  );

  return (
    <div className="flex flex-col gap-6 py-6">
      <div>
        <h2 className="text-lg font-semibold mb-3 text-zinc-900">
          Abonnements
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Utilisatrices total" value={userStats.totalUsers} />
          <StatCard
            label="Abonnées actives"
            value={userStats.subscribers}
            sub={`${userStats.subscribersPercent}% des utilisatrices`}
          />
          <StatCard
            label="En période d'essai"
            value={userStats.inTrial}
            sub={`${userStats.inTrialPercent}% des utilisatrices`}
          />
          <StatCard
            label="Essai terminé"
            value={userStats.finishedTrial}
            sub={`${userStats.convertedFromTrial} converties (${userStats.conversionRate}%)`}
          />
        </div>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-3 text-zinc-900">
          Exercices audio
        </h2>
        <div className="rounded-lg border bg-white shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-zinc-700">
                  Exercice
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium text-zinc-700">
                  Catégorie
                </th>
                <SortableHeader
                  label="Écoutes (uniques)"
                  sortKey="completions"
                  currentSortKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="% abonnées"
                  sortKey="completionsPercent"
                  currentSortKey={sortKey}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
              </tr>
            </thead>
            <tbody>
              {sortedExercises.map((ex, i) => (
                <tr
                  key={ex.id}
                  className={i % 2 === 0 ? 'bg-white' : 'bg-zinc-50'}
                >
                  <td className="px-4 py-2 font-medium text-zinc-900">
                    {ex.title}
                  </td>
                  <td className="px-4 py-2 text-zinc-600">
                    {ex.categoryTitle}
                  </td>
                  <td className="px-4 py-2 text-zinc-900">{ex.completions}</td>
                  <td className="px-4 py-2 text-zinc-900">
                    {ex.completionsPercent}%
                  </td>
                </tr>
              ))}
              {sortedExercises.length === 0 && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-6 text-center text-zinc-600"
                  >
                    Aucun exercice audio trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
